import { Box, Flex, Grid, GridItem, VStack } from '@chakra-ui/react';
import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react';
import FaceliftInput from '@/components/Atoms/FaceliftInput';
import FaceliftRadioGroup from '@/components/Atoms/FaceliftRadioGroup';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftText from '@/components/Atoms/FaceliftText';
import FaceliftTextArea from '@/components/Atoms/FaceliftTextArea';
import FaceliftDatePicker, {
	getTodayInNYTimezone,
} from '@/components/Molecules/FaceliftDatePicker';
import { Stepper, type StepperStep } from '@/components/Organisms/Stepper';
import { useTourContext } from '@/store/tourContext';
import { formatDateToString } from '@/utils/dateUtils';

export interface DemoReturnFormHandle {
	goToStep: (step: number) => void;
}

/**
 * Demo-only Return Order form.
 * - Uses static mock data (no API calls, no validations).
 * - Exposes stable refs for the tutorial system (Returns*Ref keys).
 * - The goal is purely to drive the guided tour, not to submit real data.
 */
const DemoReturnFormInner = (
	_: unknown,
	ref: React.Ref<DemoReturnFormHandle | null>,
) => {
	const [currentStep, setCurrentStep] = useState(1);
	const { registerElement } = useTourContext();
	const stepperContainerRef = useRef<HTMLDivElement | null>(null);

	// Refs for tutorial steps (these names must match backend "reference" keys)
	const [
		patientFormRef,
		questionnaireRef,
		tissueIdFieldRef,
		expirationDateFieldRef,
		dateReturnedFieldRef,
		newShippingDateFieldRef,
	] = [
		useRef<HTMLDivElement | null>(null),
		useRef<HTMLDivElement | null>(null),
		useRef<HTMLDivElement | null>(null),
		useRef<HTMLDivElement | null>(null),
		useRef<HTMLDivElement | null>(null),
		useRef<HTMLDivElement | null>(null),
		useRef<HTMLButtonElement | null>(null),
	];

	// Expose goToStep method via ref
	useImperativeHandle(
		ref,
		() => ({
			goToStep: (step: number) => {
				// Ensure step is within valid range (1-3 for Returns form)
				const validStep = Math.min(Math.max(step, 1), 3);
				setCurrentStep(validStep);
			},
		}),
		[],
	);

	// Register demo Return elements with the tour system whenever the step changes.
	useEffect(() => {
		// Register patient form ref (step 1 - Customer Information)
		if (patientFormRef.current && currentStep === 1) {
			registerElement('ReturnsPatientFormRef', patientFormRef.current);
		}

		// Register questionnaire ref (step 2 - Compliance Checklist)
		if (questionnaireRef.current && currentStep === 2) {
			registerElement('ReturnsQuestionnaireRef', questionnaireRef.current);
		}

		// Register tissue detail fields (step 3)
		if (currentStep === 3) {
			if (tissueIdFieldRef.current) {
				registerElement('ReturnsTissueIdFieldRef', tissueIdFieldRef.current);
			}
			if (expirationDateFieldRef.current) {
				registerElement(
					'ReturnsExpirationDateFieldRef',
					expirationDateFieldRef.current,
				);
			}
			if (dateReturnedFieldRef.current) {
				registerElement(
					'ReturnsDateReturnedFieldRef',
					dateReturnedFieldRef.current,
				);
			}
			if (newShippingDateFieldRef.current) {
				registerElement(
					'ReturnsNewShippingDateFieldRef',
					newShippingDateFieldRef.current,
				);
			}
			// Find the Submit button from the Stepper component
			// The Stepper renders the submit button when on the last step (submitButtonText="Submit")
			if (stepperContainerRef.current) {
				// Use setTimeout to ensure the Stepper has rendered the button
				setTimeout(() => {
					const buttons = stepperContainerRef.current?.querySelectorAll(
						'button[type="submit"]',
					);
					if (buttons && buttons.length > 0) {
						const submitButton = Array.from(buttons).find(
							(btn) => btn.textContent?.trim() === 'Submit',
						) as HTMLButtonElement;
						if (submitButton) {
							registerElement('ReturnsSubmitButtonRef', submitButton);
						}
					}
				}, 100);
			}
		}

		return () => {
			// Clean up refs when step changes or component unmounts
			if (currentStep !== 1) {
				registerElement('ReturnsPatientFormRef', null);
			}
			if (currentStep !== 2) {
				registerElement('ReturnsQuestionnaireRef', null);
			}
			if (currentStep !== 3) {
				registerElement('ReturnsTissueIdFieldRef', null);
				registerElement('ReturnsExpirationDateFieldRef', null);
				registerElement('ReturnsDateReturnedFieldRef', null);
				registerElement('ReturnsNewShippingDateFieldRef', null);
				registerElement('ReturnsSubmitButtonRef', null);
			}
		};
	}, [currentStep, registerElement]);

	// Mock data
	const mockClinics: SelectItem[] = [
		{ label: 'A Podiatry Clinic', value: 'clinic-1' },
		{ label: 'Podiatry Care Clinic', value: 'clinic-2' },
		{ label: 'Advanced Foot & Ankle', value: 'clinic-3' },
	];

	const mockTissues: SelectItem[] = [
		{ label: 'TID-1001', value: 'TID-1001' },
		{ label: 'TID-1002', value: 'TID-1002' },
		{ label: 'TID-1003', value: 'TID-1003' },
	];

	const radioItems = [
		{ label: 'Yes', value: 'true' },
		{ label: 'No', value: 'false' },
	];

	const complianceQuestions = [
		{
			field: 'ta_hastheproductbeeninyourpossessionupon',
			question: 'Has the product been in your possession since receiving it?',
		},
		{
			field: 'ta_hastheproductbeenstoredaccordingtothemanu',
			question:
				"Has the product been stored according to the manufacturer's specifications?",
		},
		{
			field: 'ta_hastheproductbeensecurelystoredbyauthoriz',
			question:
				'Has the product been securely stored by authorized clinicians?',
		},
		{
			field: 'ta_hasanyproductbeenusedoropen',
			question: 'Has any product been used or open?',
		},
		{
			field: 'ta_istheproductintheoriginalunmarkedandundam',
			question:
				'Is the product in the original unmarked and undamaged packaging?',
		},
	];

	const stepperSteps: StepperStep[] = [
		{
			name: 'Patient Information',
			element: (
				<Box mb={6} ref={patientFormRef}>
					<FaceliftText
						fontSize={{ base: 'sm', md: 'md' }}
						fontWeight="semibold"
						mb={{ base: 4, md: 6 }}
						fontStyle="italic"
					>
						Patient Information
					</FaceliftText>
					<Grid
						templateColumns={{
							base: '1fr',
							sm: 'repeat(1, 1fr)',
							md: 'repeat(3, 1fr)',
						}}
						gap={{ base: 4, sm: 5, md: 6 }}
						alignItems="start"
					>
						<GridItem>
							<FaceliftSelect<SelectItem, false>
								topLabel="Customer Name"
								required
								placeholder="Type Customer Name"
								items={mockClinics}
								value={mockClinics[0]}
								isSearchable
							/>
						</GridItem>
						<GridItem>
							<FaceliftInput
								topLabel="Street Address"
								placeholder="Type Street Address"
								value="123 Main Street"
								readOnly
							/>
						</GridItem>
						<GridItem>
							<FaceliftInput
								topLabel="City"
								placeholder="City"
								value="New York"
								readOnly
							/>
						</GridItem>
						<GridItem>
							<FaceliftInput
								topLabel="Zip/Postal Code"
								placeholder="0000"
								value="10001"
								readOnly
							/>
						</GridItem>
						<GridItem>
							<FaceliftInput
								topLabel="Phone"
								placeholder="000-000-0000"
								value="555-123-4567"
								readOnly
							/>
						</GridItem>
						<GridItem>
							<FaceliftInput
								topLabel="Contact"
								placeholder="xyz@email.com"
								type="email"
								value="contact@clinic.com"
								readOnly
							/>
						</GridItem>
					</Grid>
				</Box>
			),
		},
		{
			name: 'Compliance Checklist',
			element: (
				<Box mb={-8} ref={questionnaireRef}>
					<FaceliftText
						fontSize={{ base: 'sm', md: 'md' }}
						fontWeight="semibold"
						mb={{ base: 4, md: 6 }}
						fontStyle="italic"
					>
						Please Complete to the best of your knowledge.
					</FaceliftText>
					<VStack gap={3} align="stretch" mx={2}>
						{complianceQuestions.map((item, index) => (
							<Box key={item.field}>
								<Flex
									direction={{ base: 'column', md: 'row' }}
									justify="space-between"
									align={{ base: 'flex-start', md: 'center' }}
									gap={{ base: 2, md: 4 }}
								>
									<FaceliftText
										fontWeight="normal"
										flex={1}
										minW={0}
										wordBreak="break-word"
									>
										{index + 1}. {item.question}
									</FaceliftText>
									<Box flexShrink={0} ml={{ base: 0, md: 4 }}>
										<FaceliftRadioGroup
											value="true"
											onValueChange={() => {}}
											items={radioItems}
											gap={4}
										/>
									</Box>
								</Flex>
							</Box>
						))}
					</VStack>
				</Box>
			),
		},
		{
			name: 'Tissue Details',
			element: (
				<Box mb={-4}>
					<FaceliftText
						fontSize="sm"
						fontWeight="semibold"
						mb={2}
						fontStyle="italic"
					>
						Tissue Details
					</FaceliftText>
					<VStack gap={3} align="stretch">
						<Grid
							templateColumns={{
								base: '1fr',
								md: 'repeat(2, 1fr)',
							}}
							gap={3}
							alignItems="end"
						>
							<GridItem ref={tissueIdFieldRef}>
								<FaceliftSelect<SelectItem, false>
									topLabel="Tissue ID"
									required
									placeholder="Select Tissue ID"
									items={mockTissues}
									value={mockTissues[0]}
								/>
							</GridItem>
							<GridItem ref={expirationDateFieldRef}>
								<FaceliftText fontSize="xs" mb={1} fontWeight="medium">
									Expiration Date
								</FaceliftText>
								<FaceliftDatePicker
									placeholder="mm/dd/yyyy"
									value={formatDateToString(getTodayInNYTimezone())}
									minDate={getTodayInNYTimezone()}
									defaultToToday
								/>
							</GridItem>
						</Grid>
						<Grid
							templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
							gap={3}
							mt={2}
						>
							<GridItem ref={dateReturnedFieldRef}>
								<FaceliftText fontSize="xs" mb={1} fontWeight="medium">
									Date Return/Transferred
								</FaceliftText>
								<FaceliftDatePicker
									placeholder="mm/dd/yyyy"
									value={formatDateToString(getTodayInNYTimezone())}
									minDate={getTodayInNYTimezone()}
									defaultToToday
								/>
							</GridItem>
							<GridItem ref={newShippingDateFieldRef}>
								<FaceliftText fontSize="xs" mb={1} fontWeight="medium">
									New Shipping Date Requested
								</FaceliftText>
								<FaceliftDatePicker
									placeholder="mm/dd/yyyy"
									value={formatDateToString(getTodayInNYTimezone())}
									minDate={getTodayInNYTimezone()}
									defaultToToday
								/>
							</GridItem>
						</Grid>
						<FaceliftText fontSize="xs" fontStyle="italic" mt={2}>
							Please use the comments space below for additional items being
							returned.
						</FaceliftText>
						<Box>
							<FaceliftText fontSize="xs" mb={1}>
								<strong>Return Policy:</strong>
							</FaceliftText>
							<FaceliftText fontSize="xs" color="gray.600" mb={2}>
								Customer may return product to MindSight within 30 days of the
								original invoice date. No goods shall be returned for credit
								without first obtaining authorization from Seller's Customer
								Service department. In order for the product to qualify for a
								reimbursement or credit of the amount paid by customer, product
								must be in its original condition, unopened, have no markings,
								and have confirmation that it was properly stored. Frozen tissue
								are not able to be accepted for return.
							</FaceliftText>
						</Box>
						<FaceliftTextArea
							placeholder="Type Here"
							rows={3}
							minHeight="50px"
							defaultValue=""
						/>
					</VStack>
				</Box>
			),
		},
	];

	return (
		<Box pt={6} ref={stepperContainerRef}>
			<Stepper
				steps={stepperSteps}
				controlButtonsPosition="left"
				stepOverride={currentStep}
				onStepChange={(step) => {
					setCurrentStep(step);
				}}
				validateStep={async () => true}
				submitButtonText="Submit"
				isNextDisabled={false}
				onSubmit={async () => {
					// Demo form doesn't actually submit
					console.log('Demo form submitted');
				}}
			/>
		</Box>
	);
};

export const DemoReturnForm = forwardRef<DemoReturnFormHandle | null, unknown>(
	DemoReturnFormInner,
);

export default DemoReturnForm;
